import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { Loader2, MapPin, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { extractPropertyCoordinates, formatCoordinateInput, getFallbackCenter } from "@/lib/propertyMapUtils";

const LazyLeafletMapCanvas = lazy(() => import("@/components/maps/LeafletMapCanvas"));

const MIN_QUERY_LENGTH = 3;

export const PropertyLocationPickerSection = ({
  lang,
  copy,
  regionCode,
  subAreaCode,
  latitudeValue,
  longitudeValue,
  onCoordinatesChange,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const selectedCoordinates = useMemo(
    () => extractPropertyCoordinates({ latitude: latitudeValue, longitude: longitudeValue }),
    [latitudeValue, longitudeValue],
  );

  const fallbackCenter = useMemo(
    () => getFallbackCenter(regionCode, subAreaCode),
    [regionCode, subAreaCode],
  );

  const mapCenter = selectedCoordinates || fallbackCenter;
  const mapZoom = selectedCoordinates ? 14 : 10;

  const displayLatitude = selectedCoordinates ? formatCoordinateInput(selectedCoordinates.lat) : "";
  const displayLongitude = selectedCoordinates ? formatCoordinateInput(selectedCoordinates.lng) : "";

  useEffect(() => {
    const normalized = searchValue.trim();
    if (normalized.length < MIN_QUERY_LENGTH) {
      setSearchResults([]);
      setSearching(false);
      return;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(async () => {
      try {
        setSearching(true);
        const params = new URLSearchParams({
          q: normalized,
          format: "jsonv2",
          countrycodes: "sa",
          addressdetails: "1",
          limit: "6",
          "accept-language": lang === "ar" ? "ar" : "en",
        });
        const response = await fetch(`https://nominatim.openstreetmap.org/search?${params.toString()}`, {
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error("geocoding_failed");
        }
        const payload = await response.json();
        setSearchResults(
          (payload || []).map((item) => ({
            id: item.place_id,
            label: item.display_name,
            lat: Number(item.lat),
            lng: Number(item.lon),
          })),
        );
      } catch (error) {
        if (error.name !== "AbortError") {
          setSearchResults([]);
        }
      } finally {
        setSearching(false);
      }
    }, 450);

    return () => {
      controller.abort();
      clearTimeout(timeoutId);
    };
  }, [lang, searchValue]);

  const applyCoordinates = (lat, lng) => {
    onCoordinatesChange({
      latitude: Number(Number(lat).toFixed(6)),
      longitude: Number(Number(lng).toFixed(6)),
    });
  };

  const clearCoordinates = () => {
    onCoordinatesChange({ latitude: null, longitude: null });
  };

  return (
    <section className="dashboard-property-map-stack-fix space-y-3 rounded-2xl border border-gold-500/20 bg-amber-50/30 p-4 md:col-span-2" data-testid="dashboard-property-map-section">
      <div className="space-y-1">
        <h3 className="text-base font-semibold text-stone-900" data-testid="dashboard-property-map-title">
          {copy.sectionTitle}
        </h3>
        <p className="text-xs text-stone-600" data-testid="dashboard-property-map-subtitle">
          {copy.sectionSubtitle}
        </p>
      </div>

      <div className="relative" data-testid="dashboard-property-map-search-wrapper">
        <Search className="pointer-events-none absolute inset-y-0 left-3 my-auto h-4 w-4 text-stone-500 rtl:left-auto rtl:right-3" />
        <Input
          value={searchValue}
          onChange={(event) => setSearchValue(event.target.value)}
          placeholder={copy.searchPlaceholder}
          className="pl-9 rtl:pr-9"
          data-testid="dashboard-property-map-search-input"
        />

        {searching && (
          <Loader2 className="absolute inset-y-0 right-3 my-auto h-4 w-4 animate-spin text-stone-500 rtl:right-auto rtl:left-3" data-testid="dashboard-property-map-search-loading" />
        )}

        {searchResults.length > 0 && (
          <div className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-xl border border-stone-200 bg-white shadow-xl" data-testid="dashboard-property-map-search-results">
            {searchResults.map((result) => (
              <button
                key={result.id}
                type="button"
                className="block w-full border-b border-stone-100 px-3 py-2 text-start text-sm text-stone-700 hover:bg-stone-50 last:border-b-0"
                onClick={() => {
                  applyCoordinates(result.lat, result.lng);
                  setSearchValue(result.label);
                  setSearchResults([]);
                }}
                data-testid={`dashboard-property-map-search-result-${result.id}`}
              >
                {result.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="h-[340px] overflow-hidden rounded-2xl border border-gold-500/30 bg-stone-100" data-testid="dashboard-property-map-canvas-wrapper">
        <Suspense
          fallback={
            <div className="flex h-full items-center justify-center text-sm text-stone-600" data-testid="dashboard-property-map-loading-state">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {copy.mapLoading}
            </div>
          }
        >
          <LazyLeafletMapCanvas
            center={mapCenter}
            markerPosition={selectedCoordinates}
            zoom={mapZoom}
            onPick={({ lat, lng }) => applyCoordinates(lat, lng)}
            onMarkerDrag={({ lat, lng }) => applyCoordinates(lat, lng)}
          />
        </Suspense>
      </div>

      <div className="flex flex-wrap items-center gap-3" data-testid="dashboard-property-map-coordinates-row">
        <span className="inline-flex items-center gap-1 rounded-full border border-gold-500/40 bg-white px-3 py-1.5 text-xs text-stone-700" data-testid="dashboard-property-map-latitude-chip">
          <MapPin size={12} /> {copy.latitude}: {displayLatitude || "--"}
        </span>
        <span className="inline-flex items-center gap-1 rounded-full border border-gold-500/40 bg-white px-3 py-1.5 text-xs text-stone-700" data-testid="dashboard-property-map-longitude-chip">
          <MapPin size={12} /> {copy.longitude}: {displayLongitude || "--"}
        </span>

        {selectedCoordinates && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={clearCoordinates}
            className="rounded-full border-stone-300"
            data-testid="dashboard-property-map-clear-button"
          >
            <X size={14} /> {copy.clearPin}
          </Button>
        )}
      </div>
    </section>
  );
};
